import type { Listing, ListingStepType } from "@repo/database";
import {
  buildStepUrlFromPathname,
  getStepPathname,
  listingEditorStepsMap,
} from "./listing-editor-steps";
import { listingPublishReadiness } from "./listing-readiness";

export type ListingWizardProgressInput = Partial<Listing> & {
  step: ListingStepType;
  status?: Listing["status"];
  business?: {
    address?: {
      city?: string | null;
      state?: string | null;
      postalCode?: string | null;
      country?: { code?: string | null } | null;
    } | null;
  } | null;
};

/** Furthest setup step the draft has reached, including after a rewind to location. */
export function resumeListingWizardStep(
  listing: ListingWizardProgressInput,
): ListingStepType {
  const current = reachableStep(listing, listing.step);
  const last = listing.lastCompletedStep;
  if (!last || !listingEditorStepsMap[last]) return current;

  const nextId = listingEditorStepsMap[last].getNextStep(listing);
  if (!nextId) return current;

  const next = reachableStep(listing, nextId);
  const currentRank = listingEditorStepsMap[current].hierarchy;
  const nextRank = listingEditorStepsMap[next].hierarchy;
  return nextRank > currentRank ? next : current;
}

export function listingWizardHref(
  listingId: number,
  step: string,
  returnTo?: string | null,
): string {
  const known =
    step in listingEditorStepsMap ? (step as ListingStepType) : "LOCATION";
  const path = buildStepUrlFromPathname(listingId, getStepPathname(known));
  if (!returnTo?.startsWith("/os/")) return path;
  return `${path}?returnTo=${encodeURIComponent(returnTo)}`;
}

/** Drafts that still owe required publish fields should reopen the wizard. */
export function inventoryListingEntry(
  listing: ListingWizardProgressInput,
  photoCount: number,
): { resumeStep: ListingStepType; creationInProgress: boolean } {
  const resumeStep = resumeListingWizardStep(listing);
  if (listing.status !== "DRAFT") {
    return { resumeStep, creationInProgress: false };
  }

  const address = listing.business?.address;
  const ready = listingPublishReadiness({
    title: listing.title ?? null,
    businessTypeId: listing.businessTypeId ?? null,
    saleType: listing.saleType ?? null,
    city: address?.city ?? null,
    state: address?.state ?? null,
    postalCode: address?.postalCode ?? null,
    establishedAt: listing.establishedAt ?? null,
    pitch: listing.pitch ?? null,
    description: listing.description ?? null,
    askingPrice: listing.askingPrice ?? null,
    revenue: listing.revenue ?? null,
    sde: listing.sde ?? null,
    ebitda: listing.ebitda ?? null,
    profit: listing.profit ?? null,
    photoCount,
    propertyKind: listing.propertyKind ?? null,
    rentAmount: listing.rentAmount ?? null,
    leaseExpiration: listing.leaseExpiration ?? null,
    employeeCount: listing.employeeCount ?? null,
    ownerWeeklyHours: listing.ownerWeeklyHours ?? null,
    financingAvailable: listing.financingAvailable ?? null,
    sbaPrequalified: listing.sbaPrequalified ?? null,
    minimumDownPayment: listing.minimumDownPayment ?? null,
    buyerCanAssumeLoan: listing.buyerCanAssumeLoan ?? null,
    trainingAvailable: listing.trainingAvailable ?? null,
    trainingDuration: listing.trainingDuration ?? null,
    websiteUrl: listing.websiteUrl ?? null,
  }).readyToPublish;

  return { resumeStep, creationInProgress: !ready };
}

function reachableStep(
  listing: ListingWizardProgressInput,
  step: ListingStepType,
): ListingStepType {
  if (listing.status === "DRAFT" && (step === "CHECKS" || step === "FINISH")) {
    return "REVIEW";
  }

  const data = listingEditorStepsMap[step];
  if (!data) return listing.step;
  if (data.openConditions && !data.openConditions(listing)) {
    const previous = data.getPreviousStep(listing);
    return previous ? reachableStep(listing, previous) : listing.step;
  }
  return step;
}
